import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, Clock, Loader2, CheckCircle2, AlertTriangle, TrendingUp, Download, ChevronDown, Star, Sparkles } from "lucide-react";
import { publicApi, mediaUrl } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import MarketingConsent from "./MarketingConsent";
import Seo, { faqSchema } from "./Seo";

const SUITE = [
  { slug: "business-health-score", title: "Business Growth Health Check™", blurb: "Is your business financially healthy?" },
  { slug: "owner-independence-score", title: "Owner Independence Score™", blurb: "Could your business thrive without you?" },
  { slug: "growth-readiness-score", title: "Growth Readiness Assessment™", blurb: "Are you truly ready to scale?" },
  { slug: "financial-awareness-score", title: "Financial Awareness Score™", blurb: "Do you truly understand your numbers?" },
];

const TRAFFIC = {
  green: { dot: "bg-teal", label: "Strong", chip: "bg-teal/10 text-teal" },
  amber: { dot: "bg-amber-500", label: "Needs attention", chip: "bg-amber-50 text-amber-600" },
  red: { dot: "bg-red-500", label: "Priority", chip: "bg-red-50 text-red-600" },
};

const ScoreCircle = ({ score }) => {
  const r = 52, c = 2 * Math.PI * r, off = c - (score / 100) * c;
  const col = score >= 60 ? "#0d9488" : score >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <svg width="140" height="140" viewBox="0 0 140 140" className="shrink-0">
      <circle cx="70" cy="70" r={r} fill="none" stroke="#e2e8f0" strokeWidth="12" />
      <circle cx="70" cy="70" r={r} fill="none" stroke={col} strokeWidth="12" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={off} transform="rotate(-90 70 70)" />
      <text x="70" y="66" textAnchor="middle" fontSize="34" fontWeight="800" fill="#0f172a">{score}</text>
      <text x="70" y="90" textAnchor="middle" fontSize="13" fill="#64748b">out of 100</text>
    </svg>
  );
};

const Bar = ({ pct }) => {
  const col = pct >= 60 ? "bg-teal" : pct >= 40 ? "bg-amber-500" : "bg-red-500";
  return <div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${col}`} style={{ width: `${pct}%` }} /></div>;
};

export default function AssessmentRunner({ slug }) {
  const { toast } = useToast();
  const [cfg, setCfg] = useState(null);
  const [stage, setStage] = useState("intro"); // intro | questions | details | results
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [details, setDetails] = useState({ name: "", email: "", business_name: "", phone: "", marketing_consent: false, company_website: "" });
  const [financials, setFinancials] = useState({ turnover: "", profit: "", sales_pct: "", customers_pct: "", hours: "", revenue_drop: "" });
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [rating, setRating] = useState(null);
  const [openNum, setOpenNum] = useState(null);

  useEffect(() => { publicApi.getAssessment(slug).then(setCfg).catch(() => setCfg(false)); window.scrollTo(0, 0); }, [slug]);
  useEffect(() => { window.scrollTo(0, 0); }, [stage, step]);
  useEffect(() => { publicApi.googleRating().then(setRating).catch(() => {}); }, []);

  if (cfg === false) return <div className="py-24 text-center text-slate-400">This assessment isn't available right now.</div>;
  if (!cfg) return <div className="py-24 text-center text-slate-400"><Loader2 className="animate-scan animate-spin mx-auto" /></div>;

  const sections = cfg.sections || [];
  const totalSteps = sections.length;
  const section = sections[step];
  const setAns = (sk, qi, points) => setAnswers((a) => { const arr = [...(a[sk] || Array(sections.find((s) => s.key === sk).questions.length).fill(null))]; arr[qi] = points; return { ...a, [sk]: arr }; });
  const sectionComplete = section ? (answers[section.key] || []).filter((v) => v !== null && v !== undefined).length === section.questions.length : false;

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await publicApi.submitAssessment(slug, { ...details, answers, financials: cfg.financial_inputs ? financials : null });
      setReport(res.report);
      setStage("results");
    } catch {
      toast({ title: "Something went wrong", description: "Please check your details and try again.", variant: "destructive" });
    } finally { setSubmitting(false); }
  };

  const startAssessment = () => { publicApi.trackAssessment(slug, "start"); setStage("questions"); };
  const nextSection = () => {
    publicApi.trackAssessment(slug, "section_complete", step);
    if (step + 1 < totalSteps) setStep(step + 1); else setStage("details");
  };
  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const blob = await publicApi.assessmentPdf(slug, {
        name: details.name, business_name: details.business_name,
        answers, financials: cfg.financial_inputs ? financials : null,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `${slug}-results.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast({ title: "Couldn't generate your PDF", description: "Please try again in a moment.", variant: "destructive" });
    } finally { setDownloading(false); }
  };

  const faqBlock = cfg.faqs?.length ? (
    <div className="bg-white rounded-3xl border border-navy/5 p-6 md:p-7 mt-5" data-testid="assessment-faqs">
      <h3 className="font-display font-bold text-navy mb-4">Frequently asked questions</h3>
      <div className="divide-y divide-slate-100">
        {cfg.faqs.map((f, i) => (
          <div key={i} data-testid={`assessment-faq-${i}`}>
            <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between gap-4 py-4 text-left">
              <span className="font-semibold text-navy text-sm md:text-base">{f.question}</span>
              <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
            </button>
            {openFaq === i && <p className="text-sm text-slate-500 leading-relaxed pb-4 -mt-1">{f.answer}</p>}
          </div>
        ))}
      </div>
    </div>
  ) : null;

  const topReview = rating && Array.isArray(rating.reviews)
    ? rating.reviews.filter((r) => r.text && r.text.length > 30).sort((a, b) => (b.rating || 0) - (a.rating || 0))[0]
    : null;
  const socialProof = rating && rating.rating ? (
    <div className="bg-navy rounded-3xl p-6 md:p-7 text-white mt-5" data-testid="assessment-social-proof">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="flex items-center gap-0.5">
          {[1, 2, 3, 4, 5].map((i) => (
            <Star key={i} size={16} className={i <= Math.round(rating.rating) ? "fill-orange text-orange" : "fill-white/20 text-white/20"} />
          ))}
        </span>
        <span className="text-sm font-semibold">{rating.rating.toFixed(1)} · {rating.total} Google reviews</span>
      </div>
      {topReview && (
        <blockquote className="mt-4">
          <p className="text-sm md:text-base text-slate-200 leading-relaxed italic">"{topReview.text.length > 240 ? topReview.text.slice(0, 240).trim() + "…" : topReview.text}"</p>
          <footer className="text-xs text-slate-400 mt-2 not-italic">— {topReview.author}{topReview.relative_time ? `, ${topReview.relative_time}` : ""}</footer>
        </blockquote>
      )}
      <p className="text-xs text-slate-400 mt-4">Businesses like yours trust CASS for clear, jargon-free advice that goes beyond the numbers.</p>
    </div>
  ) : null;

  return (
    <div data-testid={`assessment-runner-${slug}`}>
      <Seo title={`${cfg.title} | CASS`} description={cfg.intro} noindex={stage === "results"}
        jsonLd={cfg.faqs?.length ? faqSchema(cfg.faqs) : undefined} />
      {/* Hero */}
      <section className="bg-navy-deep text-white py-14 md:py-16">
        <div className={`mx-auto px-6 ${cfg.hero_image ? "max-w-5xl grid md:grid-cols-2 gap-8 md:gap-10 items-center" : "max-w-3xl"}`}>
          <div>
            <Link to="/more-than-numbers/business-assessment-tools" className="text-xs text-slate-400 hover:text-white inline-flex items-center gap-1 mb-4"><ArrowLeft size={13} /> All assessment tools</Link>
            <h1 className="font-display font-extrabold text-3xl md:text-5xl mb-3">{cfg.title}</h1>
            <p className="text-slate-300 text-base md:text-lg">{cfg.tagline}</p>
          </div>
          {cfg.hero_image && (
            <div className="rounded-2xl overflow-hidden shadow-2xl aspect-[4/3] bg-white/5" data-testid="assessment-hero-image">
              <img loading="lazy" decoding="async" src={mediaUrl(cfg.hero_image, 900)} alt={cfg.hero_image_alt || cfg.title} className="w-full h-full object-cover" />
            </div>
          )}
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-6">

          {stage === "intro" && (
            <div className="bg-white rounded-3xl border border-navy/5 shadow-sm p-7 md:p-10 text-center" data-testid="assessment-intro">
              <p className="text-slate-600 leading-relaxed mb-6">{cfg.intro}</p>
              <div className="flex items-center justify-center gap-2 text-sm text-slate-400 mb-8"><Clock size={16} /> {cfg.est_time} · {totalSteps} short sections · Instant personalised result</div>
              <button data-testid="assessment-start" onClick={startAssessment} className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5">{cfg.start_cta || "Start Your Assessment"} <ArrowRight size={18} /></button>
              <p className="text-xs text-slate-400 mt-6">We'll ask for your details at the end to generate and send your report. <Link to="/privacy-policy" className="text-orange hover:underline">Privacy Policy</Link>.</p>
            </div>
          )}

          {stage === "intro" && faqBlock}

          {stage === "questions" && section && (
            <div data-testid="assessment-questions">
              <div className="flex items-center justify-between text-sm font-semibold text-navy mb-2">
                <span>Step {step + 1} of {totalSteps}</span><span className="text-slate-400">{section.title}</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden mb-8"><div className="h-full bg-teal transition-all" style={{ width: `${((step) / totalSteps) * 100}%` }} /></div>

              <div className="space-y-6">
                {section.questions.map((q, qi) => (
                  <div key={qi} className="bg-white rounded-2xl border border-navy/5 p-5 md:p-6">
                    <p className="font-semibold text-navy">{q.text}</p>
                    {q.help && <p className="text-xs text-slate-400 mt-1">{q.help}</p>}
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
                      {cfg.scale.map((opt) => {
                        const on = (answers[section.key] || [])[qi] === opt.points;
                        return (
                          <button key={opt.points} data-testid={`opt-${section.key}-${qi}-${opt.points}`} onClick={() => setAns(section.key, qi, opt.points)}
                            className={`text-xs font-semibold px-2 py-3 rounded-xl border transition-colors ${on ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-teal"}`}>{opt.label}</button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-between mt-8">
                <button data-testid="assessment-back" onClick={() => step === 0 ? setStage("intro") : setStep(step - 1)} className="inline-flex items-center gap-2 text-navy font-semibold px-5 py-3 rounded-full hover:bg-slate-100"><ArrowLeft size={16} /> Back</button>
                <button data-testid="assessment-next" disabled={!sectionComplete} onClick={nextSection}
                  className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold px-7 py-3 rounded-full">
                  {step + 1 < totalSteps ? "Next" : "See my details"} <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {stage === "details" && (
            <div className="bg-white rounded-3xl border border-navy/5 shadow-sm p-7 md:p-10" data-testid="assessment-details">
              <h2 className="font-display font-extrabold text-2xl text-navy mb-1">Almost there</h2>
              <p className="text-sm text-slate-500 mb-6">Enter your details so we can generate your personalised report and email you a copy.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input required placeholder="First & last name" value={details.name} onChange={(e) => setDetails({ ...details, name: e.target.value })} data-testid="assessment-name" className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40" />
                <input required type="email" placeholder="Email" value={details.email} onChange={(e) => setDetails({ ...details, email: e.target.value })} data-testid="assessment-email" className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40" />
                <input placeholder="Business name" value={details.business_name} onChange={(e) => setDetails({ ...details, business_name: e.target.value })} data-testid="assessment-business" className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40" />
                <input placeholder="Phone (optional)" value={details.phone} onChange={(e) => setDetails({ ...details, phone: e.target.value })} data-testid="assessment-phone" className="rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-teal/40" />
              </div>

              {cfg.financial_inputs && (
                <div className="mt-6 border-t border-slate-100 pt-5">
                  <p className="text-sm font-semibold text-navy mb-1">Optional financial inputs</p>
                  <p className="text-xs text-slate-400 mb-4">Add these for an indicative estimate of the revenue linked to you. Any figure is indicative only — not a valuation or forecast.</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <input type="number" placeholder="Annual turnover (£)" value={financials.turnover} onChange={(e) => setFinancials({ ...financials, turnover: e.target.value })} data-testid="fin-turnover" className="rounded-xl border border-slate-200 px-4 py-3 text-sm" />
                    <input type="number" placeholder="% of customers you personally manage" value={financials.customers_pct} onChange={(e) => setFinancials({ ...financials, customers_pct: e.target.value })} data-testid="fin-customers" className="rounded-xl border border-slate-200 px-4 py-3 text-sm" />
                  </div>
                </div>
              )}

              <div className="absolute left-[-9999px]"><input tabIndex={-1} value={details.company_website} onChange={(e) => setDetails({ ...details, company_website: e.target.value })} /></div>
              <div className="mt-5"><MarketingConsent checked={details.marketing_consent} onChange={(v) => setDetails({ ...details, marketing_consent: v })} testId="assessment-marketing-consent" /></div>

              <div className="flex justify-between mt-7">
                <button onClick={() => { setStage("questions"); setStep(totalSteps - 1); }} className="inline-flex items-center gap-2 text-navy font-semibold px-5 py-3 rounded-full hover:bg-slate-100"><ArrowLeft size={16} /> Back</button>
                <button data-testid="assessment-submit" disabled={submitting || !details.name || !details.email} onClick={submit} className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark disabled:opacity-40 text-white font-bold px-8 py-3.5 rounded-full">
                  {submitting ? <><Loader2 size={16} className="animate-spin" /> Generating…</> : <>View Your Results <ArrowRight size={16} /></>}
                </button>
              </div>
            </div>
          )}

          {stage === "results" && report && (
            <div data-testid="assessment-results">
              <div className="bg-white rounded-3xl border border-navy/5 shadow-sm p-7 md:p-9 flex flex-col sm:flex-row items-center gap-6">
                <ScoreCircle score={report.overall_score} />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-orange">{cfg.title}</p>
                  <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy mt-1">{report.band_label}</h2>
                  <p className="text-slate-500 mt-2">{report.band_summary}</p>
                </div>
              </div>

              {report.band_explanation && (
                <div className="bg-white rounded-3xl border border-navy/5 p-6 md:p-8 mt-5" data-testid="assessment-band-explanation">
                  <h3 className="font-display font-bold text-navy mb-3">What your score means</h3>
                  <div className="space-y-3 text-slate-600 leading-relaxed">
                    {report.band_explanation.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                </div>
              )}

              {report.biggest_opportunity && (
                <div className="bg-orange/5 border border-orange/20 rounded-3xl p-6 md:p-7 mt-5" data-testid="assessment-biggest-opportunity">
                  <h3 className="font-display font-bold text-navy mb-2 flex items-center gap-2"><TrendingUp size={18} className="text-orange" /> Your biggest opportunity</h3>
                  <p className="text-slate-600 leading-relaxed">{report.biggest_opportunity}</p>
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-5 mt-5">
                <div className="bg-white rounded-3xl border border-navy/5 p-6">
                  <h3 className="font-display font-bold text-navy mb-4">Your category breakdown</h3>
                  <div className="space-y-3">
                    {(report.sections_ranked || report.sections).map((s) => (
                      <div key={s.key}><div className="flex justify-between text-sm mb-1"><span className="text-slate-600">{s.title}</span><span className="font-bold text-navy">{s.score}%</span></div><Bar pct={s.score} /></div>
                    ))}
                  </div>
                  <div className="mt-5 grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-teal/5 rounded-xl p-3"><p className="text-xs text-slate-400">Strongest</p><p className="font-semibold text-navy">{report.strongest?.title}</p></div>
                    <div className="bg-amber-50 rounded-xl p-3"><p className="text-xs text-slate-400">Needs attention</p><p className="font-semibold text-navy">{report.weakest?.title}</p></div>
                  </div>
                </div>

                <div className="space-y-5">
                  <div className="bg-white rounded-3xl border border-navy/5 p-6" data-testid="assessment-priority-risks">
                    <h3 className="font-display font-bold text-navy mb-1 flex items-center gap-2"><AlertTriangle size={17} className="text-amber-500" /> Your priority risks</h3>
                    {report.priority_risks?.length > 0 ? (
                      <>
                        <p className="text-sm text-slate-500 mb-4">The specific statements worth your attention first.</p>
                        <ul className="space-y-3">
                          {report.priority_risks.map((r, i) => (
                            <li key={i} className="border-l-2 border-amber-300 pl-3" data-testid={`assessment-risk-${i}`}>
                              <p className="font-semibold text-navy text-sm leading-snug">{r.statement}</p>
                              {r.detail && <p className="text-sm text-slate-500 leading-relaxed mt-0.5">{r.detail}</p>}
                              <p className="text-[11px] uppercase tracking-wide text-slate-400 mt-1">{r.category}</p>
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <p className="text-sm text-slate-500 mt-2">Your answers didn't flag any specific statements as a concern — a genuinely strong sign. Keep protecting what's working well.</p>
                    )}
                  </div>
                </div>
              </div>

              {report.breakdown?.some((b) => b.image) && (
                <>
                  {report.strengths?.length > 0 && (
                    <div className="bg-white rounded-3xl border border-navy/5 p-6 md:p-8 mt-5" data-testid="assessment-strengths">
                      <h3 className="font-display font-bold text-navy mb-1 flex items-center gap-2"><CheckCircle2 size={18} className="text-teal" /> Your strongest areas</h3>
                      <p className="text-sm text-slate-500 mb-4">The numbers you understand and use well — keep protecting these habits.</p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {report.strengths.map((s, i) => (
                          <div key={i} className="bg-teal/5 rounded-2xl p-4" data-testid={`assessment-strength-${i}`}>
                            <p className="font-semibold text-navy text-sm">{s.number}</p>
                            {s.why && <p className="text-sm text-slate-500 leading-relaxed mt-1">{s.why}</p>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="bg-white rounded-3xl border border-navy/5 p-6 md:p-8 mt-5" data-testid="assessment-breakdown">
                    <h3 className="font-display font-bold text-navy mb-1">Your financial awareness breakdown</h3>
                    <p className="text-sm text-slate-500 mb-5">A traffic-light view of all ten numbers. Tap any number for what it means and how to strengthen it.</p>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {report.breakdown.map((b, i) => {
                        const t = TRAFFIC[b.status] || TRAFFIC.amber;
                        const open = openNum === i;
                        return (
                          <div key={i} className="rounded-2xl border border-slate-100 overflow-hidden" data-testid={`assessment-number-${i}`}>
                            <button onClick={() => setOpenNum(open ? null : i)} className="w-full flex items-center gap-3 p-3 text-left hover:bg-slate-50 transition-colors">
                              {b.image && <img loading="lazy" decoding="async" src={b.image} alt={b.number} className="w-12 h-12 rounded-xl object-cover shrink-0 bg-navy-deep" />}
                              <span className="flex-1 min-w-0">
                                <span className="font-semibold text-navy text-sm block truncate">{b.number}</span>
                                <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2 py-0.5 rounded-full mt-1 ${t.chip}`}><span className={`w-1.5 h-1.5 rounded-full ${t.dot}`} /> {t.label}</span>
                              </span>
                              <ChevronDown size={16} className={`shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
                            </button>
                            {open && (
                              <div className="px-4 pb-4 -mt-1 space-y-2">
                                {b.why && <p className="text-sm text-slate-500 leading-relaxed">{b.why}</p>}
                                {b.recommendation && (
                                  <p className="text-sm text-navy leading-relaxed bg-orange/5 border border-orange/15 rounded-xl p-3"><span className="font-semibold">Recommended: </span>{b.recommendation}</p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}

              {report.exposure ? (
                <div className="bg-navy-deep text-white rounded-3xl p-6 mt-5" data-testid="assessment-exposure">
                  <p className="text-sm text-slate-300">Indicative revenue linked to customers you personally manage:</p>
                  <p className="font-display font-extrabold text-3xl mt-1">£{report.exposure.toLocaleString()}</p>
                  <p className="text-xs text-slate-400 mt-2">Indicative only — not a valuation or forecast.</p>
                </div>
              ) : null}

              {report.immediate_priorities?.length > 0 && (
                <div className="bg-white rounded-3xl border border-navy/5 p-6 mt-5" data-testid="assessment-action-plan">
                  <h3 className="font-display font-bold text-navy mb-1 flex items-center gap-2"><TrendingUp size={17} className="text-orange" /> The first things to fix</h3>
                  <p className="text-sm text-slate-500 mb-4">A sensible order to work through — start here, then the next, then the next.</p>
                  <div className="space-y-3">
                    {report.immediate_priorities.map((p, i) => (
                      <div key={i} className="flex gap-4 items-start bg-slate-50 rounded-2xl p-4" data-testid={`assessment-priority-${i}`}>
                        <span className="shrink-0 bg-navy text-white text-xs font-bold px-3 py-1.5 rounded-full">{p.stage}</span>
                        <div>
                          {p.statement && <p className="font-semibold text-navy text-sm">{p.statement}</p>}
                          <p className="text-sm text-slate-600 leading-relaxed">{p.action}</p>
                          <p className="text-[11px] uppercase tracking-wide text-slate-400 mt-1">{p.category}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {report.services?.length > 0 && (
                <div className="bg-slate-50 rounded-3xl border border-navy/5 p-6 mt-5">
                  <h3 className="font-display font-bold text-navy mb-1">How CASS could help</h3>
                  <p className="text-sm text-slate-500 mb-4">Based on your results, these areas of our support may be most relevant:</p>
                  <div className="flex flex-wrap gap-2">{report.services.map((s, i) => <span key={i} className="bg-white border border-navy/10 text-navy text-sm font-semibold px-3 py-1.5 rounded-full">{s}</span>)}</div>
                </div>
              )}

              {socialProof}

              <div className="bg-white rounded-3xl border border-navy/5 p-6 mt-5" data-testid="assessment-actions">
                {report.recommended_next && (
                  <div className="mb-5 pb-5 border-b border-slate-100" data-testid="assessment-recommended-next">
                    <h3 className="font-display font-bold text-navy mb-1 flex items-center gap-2"><Sparkles size={17} className="text-orange" /> Your recommended next step</h3>
                    <p className="text-sm text-slate-500 mb-4">Based on your results, the <span className="font-semibold text-navy">{report.recommended_next.title}</span> is the most useful assessment to take next{report.recommended_next.tagline ? ` — ${report.recommended_next.tagline.toLowerCase().replace(/[.?!]+$/, "")}` : ""}.</p>
                    <Link to={report.recommended_next.link} data-testid="assessment-next-cta" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold px-6 py-3 rounded-full">Take the {report.recommended_next.title} <ArrowRight size={16} /></Link>
                  </div>
                )}
                {slug === "owner-independence-score" && (
                  <div className="mb-5 pb-5 border-b border-slate-100">
                    <h3 className="font-display font-bold text-navy mb-1">Your logical next step</h3>
                    <p className="text-sm text-slate-500 mb-4">A Business Growth Health Check is a detailed diagnostic that shows exactly what to improve first to reduce how much the business depends on you.</p>
                    <div className="flex flex-wrap items-center gap-3">
                      <Link to="/business-health-check" data-testid="assessment-health-check-cta" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-6 py-3 rounded-full">Book a Business Growth Health Check <ArrowRight size={16} /></Link>
                      <Link to="/contact" data-testid="assessment-lets-talk" className="inline-flex items-center gap-2 border border-navy/15 text-navy font-bold px-6 py-3 rounded-full hover:bg-slate-50">Let's Talk</Link>
                    </div>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-3">
                  {slug !== "owner-independence-score" && (
                    <Link to="/contact" data-testid="assessment-talk" className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-6 py-3 rounded-full">Talk Through Your Results <ArrowRight size={16} /></Link>
                  )}
                  <button data-testid="assessment-download-pdf" onClick={downloadPdf} disabled={downloading} className="inline-flex items-center gap-2 border border-navy/15 text-navy font-bold px-6 py-3 rounded-full hover:bg-slate-50 disabled:opacity-50">
                    {downloading ? <><Loader2 size={15} className="animate-spin" /> Preparing…</> : <><Download size={15} /> Download PDF report</>}
                  </button>
                </div>
                <p className="text-xs text-slate-400 mt-3">A copy of your results has also been emailed to you.</p>
              </div>

              <div className="bg-slate-50 rounded-3xl border border-navy/5 p-6 md:p-7 mt-5" data-testid="assessment-suite">
                <h3 className="font-display font-bold text-navy mb-1">The CASS Business Diagnostics Suite</h3>
                <p className="text-sm text-slate-500 mb-4">Four free diagnostics that build a complete picture of your business. Explore the others:</p>
                <div className="grid sm:grid-cols-3 gap-3">
                  {SUITE.filter((s) => s.slug !== slug).map((s) => (
                    <Link key={s.slug} to={`/more-than-numbers/${s.slug}`} data-testid={`suite-link-${s.slug}`} className="group bg-white rounded-2xl border border-navy/5 p-4 hover:shadow-md hover:-translate-y-0.5 transition-all">
                      <p className="font-semibold text-navy text-sm leading-snug">{s.title}</p>
                      <p className="text-xs text-slate-500 mt-1">{s.blurb}</p>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-orange mt-2">Take assessment <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform" /></span>
                    </Link>
                  ))}
                </div>
              </div>

              {faqBlock}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
